import { Navigate, useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { useBranch } from "../../contexts/BranchContext";
import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../../services/firebase";

const routesToCheck = [
  "/deliveryguy/",
  "/transaction",
  "/dailyreport",
  "/cardfee",
  "/bonuspenality",
  "/carddistribute",
  "/wifidistribute",
  "/waterdistribute",
  "/hotelprofit",
  "/segmentCustomer",
  "/money",
  "/budget",
  "/credit",
  "/salary",
  "/status",
  "/asbeza",
  "/card",
  "/water",
  "/wifi",
];
const routesToCheckForAdmin = [
  "/branch",
  "/callcenter",
  "/callCenter",
  "/finance",
];

const SUB_ADMIN_BRANCH_IDS_KEY = "subAdminBranchIds";

// Function to fetch subAdmin branch IDs from Firestore
const fetchSubAdminBranchIds = async (uid) => {
  try {
    const subAdminDoc = await getDoc(doc(firestore, "subAdmin", uid));
    if (subAdminDoc.exists()) {
      const data = subAdminDoc.data();

      // Handle new branches format (array of objects)
      if (data.branches && Array.isArray(data.branches)) {
        return data.branches.map((branch) => branch.branchId);
      }

      // Handle old format (branchId array)
      if (data.branchId && Array.isArray(data.branchId)) {
        return data.branchId;
      }

      // Handle single branchId (fallback)
      if (data.branchId && typeof data.branchId === "string") {
        return [data.branchId];
      }
    }
    return [];
  } catch (error) {
    console.error("Error fetching subAdmin data:", error);
    return [];
  }
};

const LoadingAnimation = () => {
  const styles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
  };

  return (
    <div style={styles}>
      <CircularProgress />
    </div>
  );
};

const RequireAuth = ({ children }) => {
  const { user, validatingUser } = useAuth();
  const location = useLocation();
  const [navigationPath, setNavigationPath] = useState(null);
  const { changecallCenterId } = useBranch();

  useEffect(() => {
    console.log("Current user:", user);
    if (!user) {
      if (localStorage.getItem(SUB_ADMIN_BRANCH_IDS_KEY)) {
        console.log(
          "User logged out or became null, clearing subAdminBranchIds."
        );
        localStorage.removeItem(SUB_ADMIN_BRANCH_IDS_KEY);
      }
      return;
    }

    // Clear any previous navigation path to avoid stale redirects on re-render
    setNavigationPath(null);

    user
      .getIdTokenResult()
      .then((idTokenResult) => {
        // Default: if not subAdmin, ensure localStorage is cleared
        // This handles cases where a user's role might change without a full logout/login
        if (idTokenResult.claims.subAdmin === undefined) {
          if (localStorage.getItem(SUB_ADMIN_BRANCH_IDS_KEY)) {
            localStorage.removeItem(SUB_ADMIN_BRANCH_IDS_KEY);
          }
        }

        if (location.pathname === "/log") {
          const newRoute = `/`;
          setNavigationPath(newRoute);
        } else if (location.pathname === "/") {
          if (idTokenResult.claims.admin !== undefined) {
            if (!user.displayName) {
              setNavigationPath("/");
            } else {
              const newRoute = `/deliveryguy/${user.displayName}`;
              setNavigationPath(newRoute);
            }
          } else if (idTokenResult.claims.subAdmin !== undefined) {
            const newRoute = `/subAdminBranch`;
            // Fetch subAdmin data to get branch IDs
            fetchSubAdminBranchIds(user.uid)
              .then((branchIds) => {
                if (branchIds && branchIds.length > 0) {
                  // Store branch IDs for subAdmin
                  localStorage.setItem(
                    SUB_ADMIN_BRANCH_IDS_KEY,
                    branchIds.join(",")
                  );
                  console.log(
                    "Stored subAdmin branch IDs:",
                    branchIds,
                    "for user:",
                    user.uid
                  );
                } else {
                  console.warn(
                    "SubAdmin user does not have branch IDs. Branch IDs not stored."
                  );
                  localStorage.removeItem(SUB_ADMIN_BRANCH_IDS_KEY);
                }
              })
              .catch((error) => {
                console.error("Error fetching subAdmin branch IDs:", error);
                localStorage.removeItem(SUB_ADMIN_BRANCH_IDS_KEY);
              });
            setNavigationPath(newRoute);
          } else if (idTokenResult.claims.callCenter !== undefined) {
            const newRoute = `/service/asbeza/${user.uid}`;
            changecallCenterId(user.uid);
            setNavigationPath(newRoute);
          } else if (idTokenResult.claims.finance !== undefined) {
            const newRoute = `mainFinance/branches/${user.uid}`;
            setNavigationPath(newRoute);
          } else {
          }
        } else if (
          routesToCheckForAdmin.some((route) =>
            location.pathname.startsWith(route)
          )
        ) {
          // Logic for admin-specific routes
          if (idTokenResult.claims.admin !== undefined) {
            const newRoute = user.displayName
              ? `/deliveryguy/${user.displayName}`
              : "/";
            setNavigationPath(newRoute);
          } else if (idTokenResult.claims.callCenter !== undefined) {
            const newRoute = `/service/asbeza/${user.uid}`;
            changecallCenterId(user.uid);
            setNavigationPath(newRoute);
          } else if (idTokenResult.claims.finance !== undefined) {
            const newRoute = `/genzeb/branch`;
            setNavigationPath(newRoute);
          }
        } else if (
          routesToCheck.some((route) => location.pathname.startsWith(route))
        ) {
          // Logic for general restricted routes
          if (idTokenResult.claims.callCenter !== undefined) {
            const newRoute = `/service/asbeza/${user.uid}`;
            changecallCenterId(user.uid);
            setNavigationPath(newRoute);
          } else if (idTokenResult.claims.finance !== undefined) {
            const newRoute = `/genzeb/branch`;
            setNavigationPath(newRoute);
          }
          // If admin or subAdmin lands here, and they are not explicitly handled, they will see `children`.
          // If they should be redirected, add conditions for them. For example:
          // else if (idTokenResult.claims.admin !== undefined) { setNavigationPath("/"); }
          // else if (idTokenResult.claims.subAdmin !== undefined) { setNavigationPath("/subAdminBranch"); }
        } else if (location.pathname.startsWith("/genzeb")) {
          if (
            idTokenResult.claims.admin !== undefined ||
            idTokenResult.claims.callCenter !== undefined
          ) {
            // If admin or callCenter tries to access /genzeb, redirect to /
            // Finance users are implicitly allowed by not being redirected.
            // What about subAdmins? If they shouldn't access /genzeb, add:
            // || idTokenResult.claims.subAdmin !== undefined
            const newRoute = "/";
            setNavigationPath(newRoute);
          }
        } else if (location.pathname.startsWith("/service")) {
          if (
            idTokenResult.claims.admin !== undefined ||
            idTokenResult.claims.finance !== undefined
          ) {
            // If admin or finance tries to access /service, redirect to /
            // CallCenter users are implicitly allowed.
            // What about subAdmins? If they shouldn't access /service, add:
            const newRoute = "/";
            setNavigationPath(newRoute);
          }
        }
        // If none of the above conditions that set navigationPath are met,
        // and the user is authenticated, they will render the `children`.
      })
      .catch((error) => {
        console.error("Error getting ID token result:", error);
        // Handle error, maybe redirect to login or an error page
        setNavigationPath("/login");
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname, changecallCenterId]); // changecallCenterId should be memoized with useCallback in BranchContext

  if (validatingUser) {
    return <LoadingAnimation />;
  }

  // Allow access to /login route even if not authenticated
  if (!user && location.pathname === "/login") {
    return children;
  }

  // If user is not authenticated and not on /login, redirect to /login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If a navigation path is determined by the useEffect logic, navigate
  if (navigationPath) {
    return <Navigate to={navigationPath} replace />;
  }

  // If authenticated and no specific redirection logic applies, render children
  return children;
};

export default RequireAuth;
